import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import { Label } from '@/design_system/Label/Label';
import { RadioItemYesNo } from '@/design_system/Radio/RadioItem/RadioItem';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import IconAdd from '@/icons/Add.svg';
import IconTrash from '@/icons/Trash.svg';
import { DefectFromGetMany } from '@/models/actionType';
import { useDatabaseTranslation } from '@/services/i18n';

import { BrandsMultiSelect } from '../shared/BrandsMultiSelect';

import { type ActionDefectValue } from './ActionsTable';

export const ActionDefectsInputs = ({
  defects,
  setDefects,
  isDisabled,
  defectOptions: defectOptions = [],
  showErrors,
}: {
  defects: ActionDefectValue[];
  setDefects: (defects: ActionDefectValue[]) => void;
  isDisabled?: boolean;
  defectOptions?: DefectFromGetMany[];
  showErrors?: boolean;
}) => {
  return (
    <Stack gap="0.5rem">
      <InlineText weight="medium">
        <Trans id="settings.actions.actions.drawer.defects">Linked defects</Trans>
      </InlineText>

      <Stack gap="1rem">
        {defects.map((defect) => {
          return (
            <ActionDefectInputs
              key={defect.id}
              defect={defect}
              defects={defects}
              setDefects={setDefects}
              defectOptions={defectOptions}
              showErrors={showErrors}
            />
          );
        })}
      </Stack>

      {!isDisabled && (
        <AddDefectButton defects={defects} setDefects={setDefects} defectOptions={defectOptions} />
      )}
    </Stack>
  );
};

const ActionDefectInputs = ({
  defect,
  defects,
  setDefects,
  isDisabled,
  defectOptions: defectOptions = [],
  showErrors,
}: {
  defect: ActionDefectValue;
  defects: ActionDefectValue[];
  setDefects: (defects: ActionDefectValue[]) => void;
  isDisabled?: boolean;
  defectOptions?: DefectFromGetMany[];
  showErrors?: boolean;
}) => {
  return (
    <Stack row gap="0.5rem" alignItems="start" style={{ flex: 1 }}>
      <Stack row gap="0.5rem" alignItems="end" style={{ flex: 1 }}>
        {!isDisabled && (
          <RemoveDefectButton defect={defect} defects={defects} setDefects={setDefects} />
        )}

        <LinkedDefectInput
          defect={defect}
          defects={defects}
          setDefects={setDefects}
          defectOptions={defectOptions}
          showErrors={showErrors}
        />
      </Stack>

      <BrandsInput defect={defect} defects={defects} setDefects={setDefects} />

      <MandatoryRadioButtons defect={defect} defects={defects} setDefects={setDefects} />
    </Stack>
  );
};

const RemoveDefectButton = ({
  defect,
  defects,
  setDefects,
}: {
  defect: ActionDefectValue;
  defects: ActionDefectValue[];
  setDefects: (defects: ActionDefectValue[]) => void;
}) => {
  const { t } = useLingui();

  return (
    <Button
      ariaLabel={t({
        id: 'settings.actions.defects-drawer.delete',
        message: `Remove`,
      })}
      tooltip={t({
        id: 'settings.actions.defects-drawer.delete',
        message: `Remove`,
      })}
      iconOnly
      size="large"
      variant="secondary-danger"
      onPress={() => setDefects(defects.filter((d) => d.id !== defect.id))}
    >
      <IconTrash />
    </Button>
  );
};

const LinkedDefectInput = ({
  defect,
  defects,
  setDefects,
  isDisabled,
  defectOptions: defectOptions = [],
  showErrors,
}: {
  defect: ActionDefectValue;
  defects: ActionDefectValue[];
  setDefects: (defects: ActionDefectValue[]) => void;
  isDisabled?: boolean;
  defectOptions?: DefectFromGetMany[];
  showErrors?: boolean;
}) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();

  return (
    <InputSelect
      label={t({
        id: 'settings.actions.defects-drawer.defect.label',
        message: 'Defect',
      })}
      isSearchable={false}
      aria-label={t({
        id: 'settings.actions.actions.drawer.defects.linked-defect',
        message: 'Linked defect',
      })}
      placeholder={t({
        id: 'settings.actions.actions.drawer.defects.linked-defect',
        message: 'Linked defect',
      })}
      value={defectOptions.find((option) => defect.defectTypeId === option.id)}
      options={defectOptions}
      getOptionValue={(option) => option.id}
      getOptionLabel={(option) => _db(option.name)}
      onChange={(option) => {
        return setDefects([
          ...defects.filter((d) => d.id !== defect.id),
          { ...defect, defectTypeId: option!.id, mandatory: defect.mandatory },
        ]);
      }}
      isInvalid={showErrors && !defect}
      isDisabled={isDisabled}
      style={{ flex: 1 }}
    />
  );
};

const BrandsInput = ({
  defect,
  defects,
  setDefects,
}: {
  defect: ActionDefectValue;
  defects: ActionDefectValue[];
  setDefects: (defects: ActionDefectValue[]) => void;
}) => {
  return (
    <Stack style={{ flex: 1 }}>
      <BrandsMultiSelect
        selectedBrandIds={defect.brandIds}
        setBrandIds={(brandIds) => {
          const newDefectValue = { ...defect, brandIds };

          setDefects(defects.map((d) => (d.id === defect.id ? newDefectValue : d)));
        }}
      />
    </Stack>
  );
};

const MandatoryRadioButtons = ({
  defect,
  defects,
  setDefects,
}: {
  defect: ActionDefectValue;
  defects: ActionDefectValue[];
  setDefects: (defects: ActionDefectValue[]) => void;
}) => {
  const { t } = useLingui();

  return (
    <Label
      label={t({
        id: 'settings.actions.defects-drawer.mandatory',
        message: 'Mandatory',
      })}
    >
      <RadioItemYesNo
        name="mandatory"
        value={defect.mandatory ? 'yes' : 'no'}
        onChange={(value) => {
          return setDefects(
            defects.map((d) => (d.id === defect.id ? { ...d, mandatory: value === 'yes' } : d))
          );
        }}
      />
    </Label>
  );
};

const AddDefectButton = ({
  defects,
  setDefects,
  isDisabled,
  defectOptions: defectOptions = [],
}: {
  defects: ActionDefectValue[];
  setDefects: (defects: ActionDefectValue[]) => void;
  isDisabled?: boolean;
  defectOptions?: DefectFromGetMany[];
}) => {
  return (
    <Button
      variant="link-subtle"
      size="small"
      style={{ alignSelf: 'start' }}
      onPress={() => {
        if (!defectOptions.length) return;

        return setDefects([
          ...defects,
          {
            id: crypto.randomUUID(),
            defectTypeId: defectOptions[0].id,
            mandatory: false,
            brandIds: [],
          },
        ]);
      }}
      disabled={isDisabled}
    >
      <IconAdd />
      <Trans id="settings.actions.actions.drawer.defects.add">Add a defect</Trans>
    </Button>
  );
};
